import { compile, middleware, prefixer } from 'stylis';
import { assign } from '../../../util';
import { getClassName } from './selector';
import { RuleCache } from './cache';
import { createLayer } from './layer';
import { createMiddleware, defaultStylePlugin } from './middleware';
import { serialize, stringify } from './serialize';
import { mergeStylePlugins } from './utils';
import { SPECIFICITY_TOKEN } from '../../../stylesheets';
const CASCADING_PREFIX = 'global-';
const SCOPED_PREFIX = 'rule-';
var RuleStatus;
(function(RuleStatus) {
    RuleStatus[RuleStatus["Unparsed"] = 0] = "Unparsed";
    RuleStatus[RuleStatus["Parsed"] = 1] = "Parsed";
})(RuleStatus || (RuleStatus = {}));
export const ruleCache = new RuleCache();
let activeState = null;
function getActiveState() {
    if (!activeState) {
        throw new Error('Expected rule state to exist.');
    }
    return activeState;
}
export function getActiveRule() {
    if (!activeState) {
        throw new Error('Expected rule state to exist.');
    }
    return activeState.rule;
}
function defineSnapshot(rule) {
    if (process.env.NODE_ENV === 'test') {
        let snapshot = '';
        Object.defineProperty(rule, 'snapshot', {
            get () {
                if (snapshot) {
                    return snapshot;
                }
                ruleCache.forEachDependency(rule, (dep)=>{
                    snapshot += `${dep.snapshot}\n`;
                });
                function serializeLayer(layer) {
                    snapshot += layer.rulesets.join('\n');
                    for(let i = 0; i < layer.order.length; i++){
                        const segment = layer.order[i];
                        snapshot += `@layer ${segment} {\n`;
                        serializeLayer(layer.layers[segment]);
                        snapshot += '\n}\n';
                    }
                }
                serializeLayer(rule.layer);
                return snapshot;
            }
        });
    }
}
function updateClasses(rule) {
    let classes = rule.className;
    ruleCache.forEachDependency(rule, (dep)=>{
        if (dep.className) {
            classes += ` ${dep.className}`;
        }
    });
    rule.classes = classes.trimStart();
}
function getSource(input, args) {
    if (typeof input === 'string') {
        return input;
    }
    let source = '';
    for(let i = 0; i < input.length; i++){
        source += input[i] + (args[i] || '');
    }
    return source;
}
function getKey(source, options) {
    return getClassName(source, options.global ? CASCADING_PREFIX : SCOPED_PREFIX);
}
export function configureRules(plugins = [
    defaultStylePlugin
], options, callback) {
    const plugin = mergeStylePlugins(plugins);
    const { preprocess, postprocess } = createMiddleware(plugin);
    function initializeRule(rule, options, deprecatedCss) {
        const previousState = activeState;
        activeState = {
            deprecatedCss,
            rule
        };
        let ruleset = rule.source;
        if (!options.global) {
            ruleset = `${SPECIFICITY_TOKEN} .${rule.className} { ${ruleset} }`;
        }
        if (options.layer) {
            ruleset = `@layer ${options.layer} { ${ruleset} }`;
        }
        try {
            serialize(compile(ruleset), middleware([
                preprocess,
                prefixer,
                stringify,
                postprocess
            ]));
            updateClasses(rule);
            rule.status = 1;
        } finally{
            activeState = previousState;
        }
    }
    function createRule(source, options, deprecatedCss) {
        const key = getKey(source, options);
        const cachedRule = ruleCache.get(key);
        if (cachedRule) {
            return cachedRule;
        }
        const rule = new Proxy({
            css: '',
            className: options.global ? '' : key,
            classes: '',
            dependencies: null,
            inserted: false,
            key,
            layer: createLayer(),
            merged: null,
            metadata: null,
            source,
            status: 0
        }, {
            get (target, key) {
                switch(key){
                    case 'className':
                    case 'key':
                    case 'inserted':
                    case 'source':
                    case 'status':
                        return target[key];
                    default:
                        if (target.status === 0) {
                            initializeRule(target, options, deprecatedCss);
                        }
                        return target[key];
                }
            }
        });
        defineSnapshot(rule);
        ruleCache.add(rule);
        return callback ? callback(rule) : rule;
    }
    function createCssTemplateLiteral({ global = false, layer = '' } = {}) {
        const options = {
            global,
            layer
        };
        function deprecatedCss(input, ...args) {
            const source = getSource(input, args);
            return createRule(source, options, deprecatedCss);
        }
        deprecatedCss.options = createCssTemplateLiteral;
        return deprecatedCss;
    }
    return createCssTemplateLiteral(options);
}
export function insertRule(rule) {
    ruleCache.insert(rule);
}
export function mergeRules(input) {
    const rules = input.filter(Boolean);
    if (rules.length === 0) {
        return null;
    } else if (rules.length === 1) {
        return rules[0];
    }
    const keys = rules.map(({ key })=>key);
    const key = keys.join(' ');
    const cachedRule = ruleCache.get(key);
    if (cachedRule) {
        return cachedRule;
    }
    const rule = {
        ...rules[0],
        className: '',
        classes: '',
        inserted: false,
        key,
        layer: createLayer(),
        merged: keys
    };
    let mergedDependencies = false;
    let mergedMetadata = false;
    for(let i = 1; i < rules.length; i++){
        const currentRule = rules[i];
        // Merge dependencies (last wins)
        if (currentRule.dependencies) {
            if (!rule.dependencies) {
                rule.dependencies = currentRule.dependencies;
            } else {
                if (!mergedDependencies) {
                    rule.dependencies = {
                        ...rule.dependencies
                    };
                    mergedDependencies = true;
                }
                assign(rule.dependencies, currentRule.dependencies);
            }
        }
        // Merge metadata (last wins)
        if (currentRule.metadata) {
            if (!rule.metadata) {
                rule.metadata = currentRule.metadata;
            } else {
                if (!mergedMetadata) {
                    rule.metadata = {
                        ...rule.metadata
                    };
                    mergedMetadata = true;
                }
                assign(rule.metadata, currentRule.metadata);
            }
        }
    }
    defineSnapshot(rule);
    updateClasses(rule);
    ruleCache.add(rule);
    return rule;
}
export function createDependency(key, value) {
    return function addDependency() {
        const { deprecatedCss, rule } = getActiveState();
        if (!rule.dependencies) {
            rule.dependencies = {};
        }
        if (!rule.dependencies[key]) {
            const reset = deprecatedCss.options({
                layer: 'reset'
            });
            const dependency = typeof value === 'string' ? reset(value) : value(reset);
            if (dependency.key !== rule.key) {
                rule.dependencies[key] = dependency.key;
            }
        }
    };
}

//# sourceMappingURL=rule.js.map